import React, { memo, useImperativeHandle, forwardRef, useState, useRef, useEffect } from 'react'
import styled from 'styled-components'
import { useTheme } from '@material-ui/core/styles'
import PropTypes from 'prop-types'
import { FlexByRow } from '../../styles'

import { Label } from '../Controls'

const StopWatchContainerOuter = styled.div`
  display: inline-block;
`

const StopWatchDataContainer = styled.div`
  color: ${props => props.theme.palette.primary.main};
`

export const useStopWatchTimer = onIimeChange => {
  const value = useRef(0)
  const id = useRef()

  const stop = () => {
    clearInterval(id.current)
    return value.current
  }

  const startWatch = () => {
    id.current = setInterval(() => {
      ++value.current
      onIimeChange && onIimeChange(value.current)
    }, 1000)
  }

  const start = () => {
    stop()
    value.current = 0
    startWatch()
  }

  const resume = () => {
    startWatch()
  }

  useEffect(() => {
    return () => stop()
  }, [])

  return {
    start,
    stop,
    resume,
    value: value.current
  }
}

export const StopWatchComponent = forwardRef((props, ref) => {
  const theme = useTheme()
  const [value, setValue] = useState(0)

  useImperativeHandle(ref, () => ({
    setValue: value => setValue(value)
  }))

  const seconds = value % 60
  let minutes = Math.floor(value / 60)
  const hours = Math.floor(minutes / 60)
  minutes = minutes % 60

  return (
    <StopWatchContainerOuter id={props.id}>
      <FlexByRow>
        <Label noTranslate={props.noTranslate}>
          {props.noTranslate ? 'Timer' : 'app.timer'}
        </Label>
        <StopWatchDataContainer theme={theme}>
          {`: ${hours.toString().padStart(2, '0')} | ${minutes.toString().padStart(2, '0')} | ${seconds.toString().padStart(2, '0')}`}
        </StopWatchDataContainer>
      </FlexByRow>
    </StopWatchContainerOuter>
  )
})

StopWatchComponent.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  noTranslate: PropTypes.bool
}

/**
 * Implements a stop watch control.
 * Attach a ref to this component. The call ref.current.start() to start it and ref.current.stop() to stop it. Also, ref.current.stop returns the elapsed time in seconds.
 * Call ref.current.getValue() to get the elapsed time at any time.
 *
 * @param {object} props component props
 * @returns {StopWatch} StopWatch component
 */
const StopWatch = memo(forwardRef((props, ref) => {
  const refStopWatchComponent = useRef()

  const onIimeChange = value => {
    refStopWatchComponent.current.setValue(value)
  }

  const { start, resume, stop, value } = useStopWatchTimer(onIimeChange)
    
  useImperativeHandle(ref, () => ({
    start: () => start(),
    resume: () => resume(),
    stop: () => stop(),
    getValue: () => value
  }))

  useEffect(() => {
    return () => stop()
  }, [])

  return (
    <StopWatchComponent ref={refStopWatchComponent} {...props} />
  )
}))

StopWatch.propTypes = {
  /**
   id for the control.
   */
  id: PropTypes.string,
  noTranslate: PropTypes.bool,
}

StopWatch.defaultProps = {
}

export default StopWatch